import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';

const OtpPage = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [expiresIn, setExpiresIn] = useState(300); // 5 min
  const [resending, setResending] = useState(false);
  const { verifyOtpLocal, requestOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');

  useEffect(() => {
    const timer = setInterval(() => setExpiresIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, []);

  // Redirect to login if OTP expires
  useEffect(() => {
    if (expiresIn === 0) {
      navigate('/login', { replace: true });
    }
  }, [expiresIn, navigate]);

  // Redirect to login if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/login', { replace: true });
    }
  }, [email, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      // Verify with backend (true validation - no bypass)
      const result = await verifyOtpLocal({ email, userInputCode: code });
      const staff = result?.staff;
      const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
      const isAdmin = perms.includes('admin:full') || String(staff?.role || '').toLowerCase() === 'admin';
      const canAccessTempleDashboard = isAdmin || perms.includes('reports:view') || perms.includes('temple:access') || perms.includes('analytics:view');
      const dest = canAccessTempleDashboard ? '/dashboard' : '/welcome';
      navigate(dest);
    } catch (err) {
      setError(err?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setError('');
    if (!email) {
      navigate('/login');
      return;
    }
    try {
      setResending(true);
      await requestOtp(email);
      setExpiresIn(300); // reset 5 min timer
      setError(''); // Clear any previous errors
    } catch (err) {
      setError(err?.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <PageLayout contentMaxWidth="sm" showHeaderImage={false} showOrgHeader showAdminActions={false}>
      <Typography variant="h5" gutterBottom>Enter OTP</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sent to: {email || 'your email'} • Expires in {Math.floor(expiresIn/60)}:{String(expiresIn%60).padStart(2,'0')}
      </Typography>
      <Box component="form" onSubmit={onSubmit}>
        <TextField fullWidth label="OTP Code" value={code} onChange={(e) => setCode(e.target.value.trim())} margin="normal" />
        {error && <Typography color="error" variant="body2">{error}</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
          <Button variant="outlined" type="button" onClick={onResend} disabled={resending}>
            {resending ? 'Resending...' : 'Resend OTP'}
          </Button>
          <Button variant="text" color="inherit" type="button" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default OtpPage;
